import { ChatBotConversationProps } from "@/components/lazo-ai-chat-components/ChatBotConversationProps";
import { AppBarContentProvider } from "@/contexts/AppBarContentContext";
import { SelectedChartProvider } from "@/contexts/ChartsContext";
import { CountriesProvider } from "@/contexts/CountriesContext";
import { LazoView, useLazoView } from "@/contexts/CurrentLazoViewContext";
import { FeatureTourProvider } from "@/contexts/FeatureTourContext";
import { LazoAIChatProvider } from "@/contexts/FilterConversationContext";
import { FundraisingProvider } from "@/contexts/FunderContext";
import { useLanguageContext } from "@/contexts/LanguageContext";
import { NavbarBackgroundProvider } from "@/contexts/NavbarBackgroundContext";
import { NotificationsContextProvider } from "@/contexts/NotificationsContext";
import { RightDrawerProvider } from "@/contexts/RightDrawerContext";
import { SelectedConversationProvider } from "@/contexts/SelectedConversationContext";
import { useSubscriptionPurchase } from "@/contexts/SubscriptionPurchaseContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoCompanyAccountType } from "@/enums/LazoCompanyAccountType";
import { LazoPaths, LazoPathsWithChat } from "@/enums/LazoPaths";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { useToggle } from "@/hooks/useToggle";
import i18n from "@/i18n/config";
import { AddOnModuleType, AddOnObjectKeyType } from "@/models/subscriptions/AddOn";
import { getAddOnMap } from "@/models/subscriptions/AddOnModuleMap";
import { SubscriptionPurchase } from "@/models/subscriptions/SubscriptionPurchase";
import MergeIntegrationService from "@/services/MergeIntegrationService";
import { envIsOneOf } from "@/utils/EnvironmentUtils";
import {
  AccountTree,
  Article,
  AssignmentOutlined,
  BuildOutlined,
  CheckCircle,
  Description,
  Group,
  Inventory,
  ListAlt,
  ManageSearch,
} from "@mui/icons-material";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import AccountingLogo from "../../assets/images/accounting-logo.svg";
import VaultSVG from "../../assets/images/vault.svg";
import { LazoAppContainer } from "./LazoAppContainer";
import DrawerHeaderSection from "./drawer-components/DrawerHeaderSection";
import { LazoDrawerFooter } from "./drawer-components/LazoDrawerFooter";
import ConnectAccountingModal from "./integrations/ConnectAccountingModal";
import LazoAIChatRightContainerWrapper from "./lazo-ai-chat-components/LazoAIChatRightContainerWrapper";
import { Navbar } from "./navigation/Navbar";
import { ModuleNoAccessModal } from "./paywall/ModuleNoAccessModal";
import { FullscreenLoading } from "./utils/FullscreenLoading";
import { UsersIcon } from "./utils/icons/UsersIcon";

const LazoPathsToFilter = [
  LazoPaths.DASHBOARD,
  LazoPaths.FUNDRAISING,
  LazoPaths.FUNDER_LIST_DETAIL.replace(":externalId", ""),
  LazoPaths.FUNDRAISING_ADVANCED_SEARCH.replace(":externalId", ""),
  LazoPaths.ONE_DATA_ROOM,
  LazoPaths.ONE_VIRTUAL_DATA_ROOMS_INSIDE,
  LazoPaths.VIRTUAL_DATA_ROOM_FINANCIAL_REPORT,
  LazoPaths.ONE_VIRTUAL_DATA_ROOMS_FILE,
];

const isDataRoomPath = (pathname: string): boolean =>
  pathname.startsWith(LazoPaths.ONE_DATA_ROOM) || pathname.startsWith(LazoPaths.ONE_VIRTUAL_DATA_ROOMS);

function persistentLayotEqual(oldProps: PersistentLayoutProps, newProps: PersistentLayoutProps) {
  return (
    oldProps.loadingComponent === newProps.loadingComponent &&
    oldProps.drawerItems === newProps.drawerItems &&
    // oldProps.breadcrumbs === newProps.breadcrumbs &&
    oldProps.lazoUser === newProps.lazoUser
  );
}

interface PersistentLayoutProps {
  loadingComponent: null | JSX.Element | JSX.Element[];
  drawerItems: any[];
  renderDefaultAppBar: boolean;
  lazoView: LazoView;
  lazoUser: any;
  footer: React.ReactNode | ((open: boolean) => React.ReactNode);
}

const PersistentLayout = React.memo(function PersistentLayout({
  loadingComponent,
  drawerItems,
  renderDefaultAppBar,
  lazoView,
  lazoUser,
  footer,
}: PersistentLayoutProps) {
  // Lifted state with persistence
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem("lazo_sidebar_open");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const toggleOpen = () => {
    setOpen((prev: boolean) => {
      const newState = !prev;
      localStorage.setItem("lazo_sidebar_open", JSON.stringify(newState));
      return newState;
    });
  };

  const [mobileOpen, toggleMobileOpen] = useToggle(false);

  // Drawer Header Logic
  const drawerHeader = lazoView === LazoView.LAZO_ONE ? (drawerOpen: boolean) => <DrawerHeaderSection open={drawerOpen} /> : null;

  return (
    <LazoAppContainer
      open={open}
      onToggleOpen={toggleOpen}
      mobileOpen={mobileOpen}
      onToggleMobileOpen={toggleMobileOpen}
      drawerHeader={drawerHeader}
      navbar={<Navbar />}
      hideTopSpacer={false}
      containerBackgroundColor={Colors.BLACK}
      clippedRightContainer={false}
      clippedDrawer={false}
      drawerWidth={220}
      rightContainer={
        <LazoAIChatProvider>
          <LazoAIChatRightContainerWrapper lazoUser={lazoUser} />
        </LazoAIChatProvider>
      }
      renderDefaultAppBar={renderDefaultAppBar}
      drawerItems={drawerItems}
      footer={footer}
    >
      <Suspense fallback={<Spinner sx={{ color: Colors.LAZO_PURPLE }} />}>{loadingComponent ? loadingComponent : <Outlet />}</Suspense>
    </LazoAppContainer>
  );
}, persistentLayotEqual);

export function AppContainerWrapper(componentProps: ChatBotConversationProps) {
  const location = useLocation();
  const { language } = useLanguageContext();
  const { lazoView } = useLazoView();

  const [renderDefaultAppBar, setRenderDefaultAppBar] = useState(false);
  const [openConnectIntegrationsModal, setOpenConnectIntegrationsModal] = useState(false);

  const subscriptionPurchaseData = useSubscriptionPurchase();

  const { currentCompany, currentTenantMembership, workspaceState } = useTenantMemberships();

  useEffect(() => {
    if (!location.pathname.includes(LazoPaths.DASHBOARD)) {
      setOpenConnectIntegrationsModal(false);
      return;
    }

    if (currentTenantMembership.tenant) {
      MergeIntegrationService.getIntegrations(currentTenantMembership.tenant.externalId)
        .then((r) => {
          const integrations = r.getContentOrThrowError();
          setOpenConnectIntegrationsModal(integrations.length <= 0);
          const mergeIntegration = integrations.find((integration) => integration.type === "MERGE");
          if (integrations.length > 0 && mergeIntegration) {
            MergeIntegrationService.mergeIntegrationHasFinished(mergeIntegration.externalId).then((i) => {
              const result = i.getContentOrThrowError();
              if (!result) {
                setOpenConnectIntegrationsModal(true);
              }
            });
          }
        })
        .catch((e: { message: string }) => console.log(e.message));
    }
  }, [location.pathname, currentTenantMembership.tenant]);

  useEffect(() => {
    if (lazoView !== LazoView.LAZO_PORTAL) {
      const shouldRender = LazoPathsWithChat.filter((path) => !LazoPathsToFilter.includes(path)).some((path) =>
        location.pathname.includes(path)
      );
      setRenderDefaultAppBar(shouldRender);
    } else {
      setRenderDefaultAppBar(false);
    }
  }, [location.pathname, lazoView]);

  const drawerItems = useMemo(() => {
    if (lazoView === LazoView.LAZO_PORTAL) {
      return getPortalItems();
    }
    return getItems(
      currentCompany?.lazoCompanyAccountType,
      lazoView === LazoView.LAZO_ONE
        ? subscriptionPurchaseData.subscriptionPurchase
        : ({
            subscription: { addOns: [] },
            extraAddOns: [],
          } as SubscriptionPurchase)
    );
  }, [currentCompany?.externalId, lazoView, language]);

  // Get subscription and access info (only for LAZO_ONE)
  const { loadingSubscription, accessMap, subscriptionPurchase } =
    lazoView === LazoView.LAZO_ONE
      ? subscriptionPurchaseData
      : {
          loadingSubscription: false,
          accessMap: {},
          subscriptionPurchase: null,
        };
  const isExternalUser = !workspaceState?.isLazoAdminOrKam;
  const shouldSkipDataRoomPaywall = isExternalUser && isDataRoomPath(location.pathname);
  const shouldRenderNoAccessModal = accessMap[location.pathname] === false && !shouldSkipDataRoomPaywall;

  // Only show the fullscreen overlay on the very first subscription load.
  // For subsequent refetches (e.g. tenant switches) we already have cached data, so we let the
  // page re-render silently instead of flashing a black overlay.
  const isInitialSubscriptionLoad = loadingSubscription && !subscriptionPurchase;

  const loadingComponent = isInitialSubscriptionLoad ? (
    <FullscreenLoading />
  ) : shouldRenderNoAccessModal ? (
    <ModuleNoAccessModal />
  ) : openConnectIntegrationsModal ? (
    <ConnectAccountingModal open={openConnectIntegrationsModal} isFetching={false} />
  ) : null;

  return (
    <SelectedConversationProvider>
      <NotificationsContextProvider>
        <NavbarBackgroundProvider>
          <AppBarContentProvider>
            <RightDrawerProvider>
              <SelectedChartProvider>
                <FundraisingProvider>
                  <CountriesProvider>
                    <FeatureTourProvider>
                      <PersistentLayout
                        loadingComponent={loadingComponent}
                        drawerItems={drawerItems}
                        renderDefaultAppBar={renderDefaultAppBar}
                        lazoView={lazoView}
                        lazoUser={componentProps.lazoUser}
                        footer={(open: boolean) => <LazoDrawerFooter open={open} lazoUser={componentProps.lazoUser} />}
                      />
                    </FeatureTourProvider>
                  </CountriesProvider>
                </FundraisingProvider>
              </SelectedChartProvider>
            </RightDrawerProvider>
          </AppBarContentProvider>
        </NavbarBackgroundProvider>
      </NotificationsContextProvider>
    </SelectedConversationProvider>
  );
}

function getItems(lazoCompanyAccountType: LazoCompanyAccountType, subscriptionPurchase: SubscriptionPurchase) {
  const items: any[] = [
    /* { title: i18n.t("navigation:drawer.sections.activeServices") } */
  ];
  const purchaseAddOnsMap: Record<string, any> = {};

  if (subscriptionPurchase) {
    let addOns = subscriptionPurchase.subscription.addOns;
    if (subscriptionPurchase.extraAddOns) {
      addOns = addOns.concat(subscriptionPurchase.extraAddOns);
    }

    for (let i = 0; i < addOns.length; i++) {
      const _module = addOns[i].module;
      if (!purchaseAddOnsMap[_module]) {
        purchaseAddOnsMap[_module] = [];
      }
      purchaseAddOnsMap[_module].push(addOns[i].objectKey);
    }
  }

  // Prepend Static Lazo One Items (Tasks, Documents)
  const isIndividual = lazoCompanyAccountType === LazoCompanyAccountType.INDIVIDUAL;
  if (!isIndividual) {
    items.unshift({
      key: "documents",
      label: i18n.t("navigation:drawer.lazoOne.documents"),
      icon: <Description />,
      path: LazoPaths.ONE_DOCUMENT_CENTER,
    });
    items.unshift({
      key: "my_services",
      label: i18n.t("navigation:drawer.lazoOne.myServices"),
      icon: <AssignmentOutlined />,
      path: LazoPaths.MY_SERVICES,
      items: [
        {
          key: "my_tasks",
          label: i18n.t("navigation:drawer.lazoOne.myTasks"),
          icon: <CheckCircle />,
          path: LazoPaths.TASKS,
        },
      ],
    });
  }

  // Append "Add Services"
  // items.push({
  //   key: "add_services",
  //   label: i18n.t("navigation:drawer.lazoOne.addServices"),
  //   icon: <AddCircle />,
  //   path: LazoPaths.PRODUCT,
  // });

  // Get the AddOn map with current translations
  const addOnMap = getAddOnMap();

  switch (lazoCompanyAccountType) {
    case LazoCompanyAccountType.COMPANY: {
      const lazoModule = {
        label: i18n.t("navigation:drawer.sections.accounting"),
        key: "Accounting",
        icon: <img src={AccountingLogo.src} alt="accounting-logo-icon" />,
        items: [],
      };

      lazoModule.items.push(addOnMap[AddOnModuleType.ACCOUNTING][AddOnObjectKeyType.TAX_DUE_DATES]);
      // items.push(lazoModule);

      // items.push({
      //   label: i18n.t("navigation:drawer.sections.intelligence"),
      //   key: "Intelligence",
      //   icon: <img src={DollarIcon.src} alt="dollar-logo-icon" />,
      //   items: [
      //     {
      //       key: "Smartlists",
      //       icon: <></>,
      //       label: i18n.t("navigation:drawer.items.smartlists"),
      //       path: LazoPaths.FUNDRAISING,
      //     },
      //   ],
      // });

      const roomModule = {
        label: i18n.t("navigation:drawer.sections.dataRooms"),
        key: "data-rooms",
        icon: <img src={VaultSVG.src} alt="data-room-icon" />,
        items: [],
      };

      roomModule.items.push(addOnMap[AddOnModuleType.DATA_ROOM][AddOnObjectKeyType.MASTER_DATA_ROOM]);
      roomModule.items.push(addOnMap[AddOnModuleType.VIEW_DATA_SHARED_BY_OTHERS][AddOnObjectKeyType.SHARED_SPACES]);

      items.push(roomModule);

      break;
    }
    case LazoCompanyAccountType.INDIVIDUAL: {
      // items.push({
      //   label: i18n.t("navigation:drawer.sections.intelligence"),
      //   key: "Intelligence",
      //   icon: <img src={DollarIcon.src} alt="dollar-logo-icon" />,
      //   items: [
      //     {
      //       key: "Smartlists",
      //       icon: <></>,
      //       label: i18n.t("navigation:drawer.items.smartlists"),
      //       path: LazoPaths.FUNDRAISING,
      //     },
      //   ],
      // });

      const roomModule = {
        label: i18n.t("navigation:drawer.sections.dataRooms"),
        key: "data-rooms",
        icon: <img src={VaultSVG.src} alt="data-room-icon" />,
        items: [],
      };

      roomModule.items.push(addOnMap[AddOnModuleType.DATA_ROOM][AddOnObjectKeyType.MASTER_DATA_ROOM]);

      roomModule.items.push(addOnMap[AddOnModuleType.VIEW_DATA_SHARED_BY_OTHERS][AddOnObjectKeyType.SHARED_SPACES]);

      items.push(roomModule);

      // items.push({
      //   label: i18n.t("navigation:footer.integrations"),
      //   key: "integrations",
      //   icon: <img src={IntegrationsLogo.src} alt="integrations-icon" />,
      //   items: [
      //     {
      //       key: "Integrations",
      //       icon: <></>,
      //       label: i18n.t("navigation:footer.integrations"),
      //       path: LazoPaths.INTEGRATIONS,
      //     },
      //   ],
      // });
    }
  }

  return items;
}

function getPortalItems() {
  const isQA = envIsOneOf(["qa"]);
  const items = [
    {
      key: "customers",
      label: i18n.t("navigation:drawer.portal.customers"),
      icon: <UsersIcon />,
      path: LazoPaths.PORTAL_CUSTOMERS,
    },
    {
      key: "tasks",
      label: i18n.t("navigation:drawer.portal.tasks"),
      icon: <ListAlt />,
      path: LazoPaths.TASKS_ADMIN,
    },
    {
      key: "team",
      label: i18n.t("navigation:drawer.portal.lazoTeam"),
      icon: <Group />,
      path: LazoPaths.LAZO_TEAM,
    },
    {
      key: "product_manager",
      label: i18n.t("navigation:drawer.portal.productManager"),
      icon: <Inventory />,
      path: LazoPaths.PRODUCT_MANAGER,
    },
    {
      key: "document_manager",
      label: i18n.t("navigation:drawer.portal.documentManager"),
      icon: <Article />,
      path: LazoPaths.DOCUMENT_MANAGER,
    },
    {
      key: "search_reporting",
      label: i18n.t("navigation:drawer.portal.searchAndReporting"),
      icon: <ManageSearch />,
      path: LazoPaths.SEARCH_AND_REPORTING,
    },
  ];

  if (isQA) {
    items.push({
      key: "org_chart",
      label: i18n.t("navigation:drawer.portal.orgChart"),
      icon: <AccountTree />,
      path: LazoPaths.ORG_CHART,
    });
  }

  items.push({
    key: "tools",
    label: "Tools",
    icon: <BuildOutlined />,
    path: LazoPaths.TOOLS,
  });

  return items;
}
